import { collectionToStories, Link } from "@quintype/components";
import get from "lodash/get";
import PropTypes from "prop-types";
import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useDeviceType } from "../../../../components/customHooks/useDeviceType";
import { getTemplateName, trackLoadMoreClick } from "../../../../components/utils";
import { getCategoryAndSubCategory, useTrackWidgetImpression } from "../../../../ga-utils";
import { ProgressiveHydration } from "../../../hydration-component";
import { generateNavigateSlug, getSlot, getTextColor, navigateTo } from "../../../utils/utils";
import { CollectionName } from "../../Atoms/CollectionName";
import { HeroImage } from "../../Atoms/HeroImage";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { StoryCard } from "../../Molecules/StoryCard";
import { StorycardContent } from "../../Molecules/StorycardContent";
import { StateProvider } from "../../SharedContext";

import "./dialogue-grid-col.m.css";

const loadMore = ({ isLoading, storyItems, getMoreStories, subsequentLoadCount = 6 }, pageType) => {
  if (typeof window !== "undefined") {
    const { category, subCategory } = getCategoryAndSubCategory(window.location, pageType);
    const templateName = getTemplateName(pageType);
    trackLoadMoreClick({
      linkCategory: category || "",
      category: category || "companies",
      subCategory: subCategory || "NA",
      templateName: templateName,
      pageType: pageType,
    });
  }

  if (isLoading) return;
  const limit = subsequentLoadCount;
  const offset = storyItems.length;
  getMoreStories(offset, limit);
};

const BannerWithTwoColGrid = ({
  isLoading,
  collection,
  config,
  getMoreStories,
  isolatedLoadMore,
  template,
  initialLoadCount = 6,
  isLoadMoreVisible,
  sectionSwitch = false,
  page,
  isMoreVideos,
  customSlug,
  customCollectionName,
  adType,
  showAuthorContent,
  refKey = false,
  fromStory = {},
  id = "",
}) => {
  const { theme = "", border = "", subsequentLoadCount = 4, slotConfig } = config;
  const { type = "", component } = get(slotConfig, [0], {});
  const adWidgetSlot = getSlot(type, component);
  const qtState = useSelector((state) => state);
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  let breakPoint = isAdfreePage ? 6 : 5;

  useEffect(() => {
    if (typeof window !== "undefined" && pageType === "author-page") {
      const width = window.innerWidth;
      const desktopBreakpoint = isAdfreePage ? 6 : 5;
      const mobileBreakpoint = isAdfreePage ? 4 : 3;
      breakPoint = width > 1159 ? desktopBreakpoint : mobileBreakpoint;
    }
  }, []);

  const storyItems = collectionToStories(collection);

  if (!storyItems.length) return null;

  const textColor = getTextColor(theme);

  const dispatch = useDispatch();
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));

  // Track widget impression using the hook
  const isBrowser = typeof window !== "undefined";
  const trackingOptions = {
    sectionName: get(collection, ["name"], ""),
    pageType: pageType,
    location: isBrowser ? window.location : {},
    config: qtConfig,
    story: fromStory,
    threshold: 0.2,
  };
  const componentRef = useTrackWidgetImpression(trackingOptions);
  let templateName;

  if (fromStory?.["story-template"]) {
    templateName = getTemplateName(fromStory["story-template"]);
  }else {
    templateName = getTemplateName(pageType);
  }
  const trackEvent = (linkPosition) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtConfig,
        fromStory
      );
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: linkPosition,
        section_name: collection?.name ? collection.name?.toLowerCase() : "NA",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const getLoadMore = (opts) => {
    if (!isLoadMoreVisible) return null;
    const handleReadMoreClick = () => {
      trackEvent(-1);
      navigateTo(dispatch, url);
    };

    if (opts.isBtnWithArrow) {
      return (
        <div onClick={handleReadMoreClick} style={{ cursor: "pointer" }}>
          <LoadmoreButton
            config={config}
            collection={collection}
            sectionSwitch={sectionSwitch}
            qtConfig={qtConfig}
            isBtnWithArrow={opts?.isBtnWithArrow}
          />
        </div>
      );
    }

    const url = generateNavigateSlug(collection, qtConfig);
    return (
      <ProgressiveHydration>
        <div onClick={() => loadMore(opts, page)} style={{ cursor: "pointer" }}>
          <LoadmoreButton
            config={config}
            collection={collection}
            sectionSwitch={sectionSwitch}
            qtConfig={qtConfig}
            template={template}
          />
        </div>
      </ProgressiveHydration>
    );
  };

  const handleStoryClick = (index) => {
    const position = index + 1;
    trackEvent(position);
  };

  const storyCard = (story, index) => {
    return (
      <>
        <div styleName={`cards ${textColor}`} className="cards" onClick={() => handleStoryClick(index)}>
          <StoryCard
            story={story}
            border={border}
            theme={theme}
            config={config}
          >
            <HeroImage aspectRatio={[[16, 9]]} story={story} isHorizontalWithImageLast={textColor !== "light"} />
            <StorycardContent
              theme={theme}
              border={border}
              story={story}
              config={config}
              headerLevel={3}
              showAuthorContent={showAuthorContent}
            />
          </StoryCard>
        </div>
      </>
    );
  };

  const renderCollectionName = () => {
    const handleCollectionNameClick = () => {
      trackEvent(-2);
    };

    if (collection?.["associated-metadata"]?.show_collection_name || page === "custom-event-page") {
      return (
        <div onClick={handleCollectionNameClick} style={{ cursor: "pointer" }}>
          <CollectionName
            collection={collection}
            headerLevel={2}
            customSlug={customSlug}
            customCollectionName={customCollectionName}
            showCircleIcon={false}
          />
        </div>
      );
    }
  };

  const isLightOrVideo = textColor === "light" || isMoreVideos;
  const isCustomEvent = page === "custom-event-page";
  const sliceCount = isAdfreePage ? 6 : isLightOrVideo && !isCustomEvent ? 5 : 6;
  const collectionSummary = collection?.summary || "";
  const collectionLink = collection?.slug || "";


  return (
    <div
      className={`full-width-with-padding arrow-component ${page}`}
      data-test-id="three-col-grid"
      style={{ backgroundColor: theme, color: textColor }}
      styleName={`three-col-grid-wrapper ${textColor}`}
      ref={componentRef}
      id={id}
      {...(refKey ? { ref: refKey } : {})}
    >
      <div styleName="three-col-grid">
        <div styleName="collection-name-with-read-more">
          <div styleName="collection-name-summary">
            <div styleName="brandlogo">
              <Link
                href={collectionLink}
                title="Open Dialogue"
                aria-label="collection-name"
                disableAjaxLinks={true}
              >
                <img
                  src="https://media.openthemagazine.com/openthemagazine/2026-05-15/fv2ad7as/OPEN-dialogues-logo.png"
                  width={120}
                  height={59}
                  alt={customCollectionName || "Open Dialogue"}
                  title="Open Dialogue"
                  loading="lazy"
                />
              </Link>
            </div>
            <div styleName="collection-name-summary-text">
              <div styleName="collection-name-and-readmore">
                {page !== "profile-page" || page !== "company-page" ? (
                  renderCollectionName()
                ) : (
                  <h2>Open Magazine Coverage</h2>
                )}
                {page !== "author-page" &&
                  page !== "profile-page" &&
                  getLoadMore({ isLoading, storyItems, getMoreStories, subsequentLoadCount, isBtnWithArrow: true })}
              </div>
              <Link href={collectionLink} aria-label="collection-name" disableAjaxLinks={true}>
                <p>{collectionSummary}</p>
              </Link>
            </div>
          </div>


        </div>

          <div className="three-col-wrapper" styleName={`wrapper ${textColor} ${storyItems.length === 1 ? "oneColumn" : ""} ${storyItems.length === 2 ? "twoColumn" : ""}`}>
            {storyItems.slice(0, sliceCount).map((story, index) => (
              <Fragment key={`story-3col1-${index}-${get(story, ["id"], "0")}`}>{storyCard(story, index)}</Fragment>
            ))}

            {!isAdfreePage && useDeviceType() !== "mobile" && isLightOrVideo && adWidgetSlot}
          </div>
        {getLoadMore({ isLoading, storyItems, getMoreStories, subsequentLoadCount, isBtnWithArrow: false })}
      </div>
    </div>
  );
};

BannerWithTwoColGrid.propTypes = {
  /**  collection is the array of objects which is returning by API  */
  collection: PropTypes.object.isRequired,
  /** The config for the Row */
  config: PropTypes.shape({
    theme: PropTypes.string,
    border: PropTypes.bool,
    subsequentLoadCount: PropTypes.number,
    slotConfig: PropTypes.element,
  }),
  getMoreStories: PropTypes.func,
  sectionSwitch: PropTypes.func,
  isLoadMoreVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  isolatedLoadMore: PropTypes.bool,
  initialLoadCount: PropTypes.number,
  template: PropTypes.string,
  page: PropTypes.string,
  isMoreVideos: PropTypes.bool,
  customSlug: PropTypes.string,
  customCollectionName: PropTypes.string,
  adType: PropTypes.string,
  showAuthorContent: PropTypes.bool,
  refKey: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  fromStory: PropTypes.object,
};

BannerWithTwoColGrid.defaultProps = {
  getMoreStories: () => {},
  config: {},
  collection: {},
  isolatedLoadMore: false,
  isLoading: false,
  isMoreVideos: false,
};

export default StateProvider(BannerWithTwoColGrid);
